import React, { FC } from 'react'
import Head from 'next/head'
import { useAnalytics, useSetting, useSettingDefault } from '@ui/hooks'
import { store_type } from '@libs/common/models/constant'
import { useRouter } from 'next/router'
import {
  // getImagesLink,
  imagePath,
  isBDLScriptDomains,
  isCustomGtmDomains,
  isCustomScriptDomains,
  isActiveSpeedCurve,
  useCdnBasePathImage,
  useCdnBasePath,
  videoPath
} from '@ui/helpers'
import {
  FbpScript,
  GtagScript,
  PinterestScript,
  SnapchatScript,
  TiktokPixelScript
} from '@ui/analytics'
import getConfig from 'next/config'
import { gaExcludes, thumbImage } from '@libs/client'
// const mThumb = thumbImage('w=500,h=500')

// const imageThumb = sThumb(sellpageSetting?.fallback_img?.[imgUrls[index]] || '')
//   .replace('.gif', '.webp')
//   .replace('.webp', `_${size}.webp`)
const cdnBasePath = getConfig().publicRuntimeConfig.cdnBasePath || ''

const gtmId_GA4 = getConfig().publicRuntimeConfig.gtmId || ''
const isLocal = getConfig().publicRuntimeConfig.env == 'local'

const MyHead: FC = () => {
  const router = useRouter()
  const slug = router.query.subpath?.toString()
  const settingDefault = useSettingDefault()
  const [storeSetting] = useSetting('store')
  const [sellpageSetting] = useSetting('sellpage')
  const analytics = useAnalytics()
  const isMinistore = storeSetting?.general.type === store_type.MINISTORE
  const storePageTitle =
    (storeSetting?.general?.title || storeSetting?.general?.domain?.split('.')?.[0]) ?? ''
  const storeName = storeSetting?.general?.name
  const sellpageTitle = `${storeName ? `${storeName} - ` : ''}${
    sellpageSetting?.settings?.general.title ?? ''
  }`
  const title = isMinistore && !slug ? storePageTitle : sellpageTitle
  const basepathImage = useCdnBasePathImage()
  const basepath = useCdnBasePath()

  const sThumb = thumbImage('96x96', basepathImage)
  const sellpageImage = (page: S3Types.page_setting | null) => {
    if (!page) return ''
    const defaultId = page?.variants?.default_variant_id
    const image = page?.gallery?.[0]?.includes('.mp4')
      ? sThumb(
          (page?.fallback_img?.[page?.gallery?.[0]] || '')?.replace('.gif', '.webp')
          // .replace('.webp', `_400x400.webp`)
        )
      : page?.gallery?.[0]
    return page?.variants?.items?.[defaultId]?.logo ?? image ?? ''
  }
  const description =
    isMinistore && !slug
      ? storeSetting?.general?.meta?.description
      : sellpageSetting?.settings?.general?.description
  const image =
    isMinistore && !slug ? storeSetting?.general?.logo?.image ?? '' : sellpageImage(sellpageSetting)
  const favicon = `https://${storeSetting?.general?.domain}${isLocal ? ':3000' : ''}/fv/favicon.ico`
  const url = `${storeSetting?.general?.domain}${slug ? `/${slug}` : ''}`
  const fbpPixelIds = analytics?.pixel_ids ?? []
  const tiktokPixelIds = analytics?.tiktok_ids ?? []
  const snapchatPixelIds = analytics?.snapchat_ids ?? []

  const gaIdsAdmin = !gaExcludes.includes(storeSetting?.general?.domain || '')
    ? (settingDefault?.setting?.settings.general?.ga_ids ?? '').split(',')
    : []
  const gaIds = [...(storeSetting?.analytics?.google_analytic_ids ?? []), ...(gaIdsAdmin ?? [])]
  const isThankyouPage = router.pathname?.startsWith('/thankyou')
  return (
    <Head>
      <title>{title || 'Storefront'}</title>
      <link rel="shortcut icon" href={favicon}></link>
      <link rel="apple-touch-icon" href={favicon}></link>

      <link rel="preconnect" href={`${cdnBasePath}`} />
      <link rel="preconnect" href="https://fonts.gstatic.com" />
      <link rel="preconnect" href="https://cdnjs.cloudflare.com" />
      <link rel="dns-prefetch" href={`${cdnBasePath}`} />
      <meta httpEquiv="x-dns-prefetch-control" content="on"></meta>
      {!sellpageSetting?.gallery?.[0]?.includes('.mp4') && (
        <link rel="preload" href={image ? imagePath(image, basepathImage) : ''} as="image"></link>
      )}
      {sellpageSetting?.gallery?.[0]?.includes('.mp4') && (
        <link rel="preload" href={image} as="image" />
      )}
      <meta
        key="viewport"
        name="viewport"
        content="width=device-width, initial-scale=1, user-scalable=no"
      ></meta>
      <meta key="description" name="description" content={description || 'storefront'}></meta>
      <meta key="theme-color" name="theme-color" content="#1B1B1B"></meta>
      <meta property="og:type" content={slug ? 'product' : 'website'}></meta>
      <meta property="og:title" content={title || 'storefront'}></meta>
      <meta property="og:site_name" content={title || 'storefront'}></meta>
      <meta property="og:description" content={description || 'storefront'}></meta>
      {!sellpageSetting?.gallery?.[0]?.includes('.mp4') && (
        <meta property="og:image" content={image ? imagePath(image, basepathImage) : ''}></meta>
      )}
      {sellpageSetting?.gallery?.[0]?.includes('.mp4') && (
        <meta
          property="og:video"
          content={videoPath(sellpageSetting?.gallery?.[0], basepath)}
        ></meta>
      )}

      <meta property="og:url" content={url ?? ''}></meta>
      <meta key="mobile-web-app-capable" name="mobile-web-app-capable" content="yes"></meta>
      <meta
        key="apple-mobile-web-app-capable"
        name="apple-mobile-web-app-capable"
        content="false"
      ></meta>
      <meta
        key="apple-mobile-web-app-title"
        name="apple-mobile-web-app-title"
        content={title || 'storefront'}
      ></meta>
      <meta
        name="google-site-verification"
        content={storeSetting?.analytics?.google_verification_id || ''}
      ></meta>
      <meta
        name="p:domain_verify"
        content={storeSetting?.analytics?.pinterest_verification_id || ''}
      ></meta>
      <meta
        name="facebook-domain-verification"
        content={storeSetting?.general.meta?.facebook_domain_verification || ''}
      ></meta>
      {snapchatPixelIds.length && <SnapchatScript pixel_ids={snapchatPixelIds} />}
      <PinterestScript />
      {fbpPixelIds.length && <FbpScript pixel_ids={fbpPixelIds} />}
      {tiktokPixelIds.length && <TiktokPixelScript pixel_ids={tiktokPixelIds} />}
      {/* {(gaIds.length || (isThankyouPage && googleConversionTag.length)) && (
        <GtagScript
          gaIds={gaIds}
          googleConversionTag={googleConversionTag}
          isThankyouPage={isThankyouPage}
        />
      )} */}
      {gaIds.length > 0 && <GtagScript gaIds={gaIds} />}
      {/* <GaScript gaIds={gaIds} /> */}
      {!isThankyouPage && <CustomScript url={storeSetting?.general?.domain ?? ''} />}
    </Head>
  )
}

const CustomScript = ({ url }: { url: string }) => {
  const [showBdlScript] = isBDLScriptDomains(url)
  const [showCustomGtm, gtmId] = isCustomGtmDomains(url)
  const showCustomScript = isCustomScriptDomains(url)
  const activeSpeedCurve = isActiveSpeedCurve(url)
  const isGTM = !gaExcludes.includes(url)
  return (
    <>
      {showCustomScript && (
        <script
          dangerouslySetInnerHTML={{
            __html: `window.mcAsyncInit=function(){var widgetData=localStorage.getItem("mcwidget");if(widgetData){widgetData=JSON.parse(widgetData)}else{widgetData={}}var pixelData=localStorage.getItem("mc_pixel_session_data");var checkOptin=false;if(pixelData){checkOptin=lookup(JSON.parse(pixelData),"hasOptedIn")}function lookup(obj,k){if(typeof obj!="object"){return null}var result=null;if(obj.hasOwnProperty(k)){return obj[k]}else{for(var o in obj){result=lookup(obj[o],k);if(result==null)continue;else break}}return result}function displayManychatPopup(mcAgent,widgetData,mcDomain,haveQuery){var mcDomains=[{domain:"theuspants.com",mcPopupId:12982344},{domain:"timejeans.net",mcPopupId:12982510},{domain:"fluff-shoe.mycellas.com",mcPopupId:12981308},{domain:"yourexsecret.com",mcPopupId:13469907},{domain:"jenn-bra.com",mcPopupId:13469511},{domain:"ngrok.io",mcPopupId:12981308},];for(var mcI in mcDomains){if(mcDomains.hasOwnProperty(mcI)){var mcDom=mcDomains[mcI];if(mcDomain.includes(mcDom.domain)&&haveQuery){var popUp1297=lookup(widgetData,""+mcDom.mcPopupId);var popupClosed24=false;if(popUp1297){popupClosed24=lookup(popUp1297,"manualClose")}if(!popupClosed24){mcAgent.getWidget(mcDom.mcPopupId).open()}break}}}}setTimeout(function(){if(!checkOptin){var cDomain=window.location.href;var haveQuery=cDomain.includes("?");displayManychatPopup(MC,widgetData,cDomain,haveQuery)}},10000)};`
          }}
        ></script>
      )}
      {showBdlScript && (
        <>
          <script src="https://www.bm22trk.com/scripts/sdk/everflow.js" key="bdlScriptJs"></script>
          <script
            dangerouslySetInnerHTML={{
              __html: `EF.click({offer_id:EF.urlParameter('oid'),affiliate_id:EF.urlParameter('affid'),sub1:EF.urlParameter('sub1'),sub2:EF.urlParameter('sub2'),sub3:EF.urlParameter('sub3'),sub4:EF.urlParameter('sub4'),sub5:EF.urlParameter('sub5'),uid:EF.urlParameter('uid'),source_id:EF.urlParameter('source_id'),transaction_id:EF.urlParameter('_ef_transaction_id'),});`
            }}
          />
          <script
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-NB2CMDC');`
            }}
          ></script>
        </>
      )}
      {showCustomGtm && (
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${gtmId}');`
          }}
        ></script>
      )}
      {isGTM && (
        <script
          defer
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${gtmId_GA4}');`
          }}
        ></script>
      )}

      {activeSpeedCurve && (
        <script
          dangerouslySetInnerHTML={{
            __html: `LUX=(function(){var a=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMarks)?LUX.gaMarks:[]);var d=("undefined"!==typeof(LUX)&&"undefined"!==typeof(LUX.gaMeasures)?LUX.gaMeasures:[]);var j="LUX_start";var k=window.performance;var l=("undefined"!==typeof(LUX)&&LUX.ns?LUX.ns:(Date.now?Date.now():+(new Date())));if(k&&k.timing&&k.timing.navigationStart){l=k.timing.navigationStart}function f(){if(k&&k.now){return k.now()}var o=Date.now?Date.now():+(new Date());return o-l}function b(n){if(k){if(k.mark){return k.mark(n)}else{if(k.webkitMark){return k.webkitMark(n)}}}a.push({name:n,entryType:"mark",startTime:f(),duration:0});return}function m(p,t,n){if("undefined"===typeof(t)&&h(j)){t=j}if(k){if(k.measure){if(t){if(n){return k.measure(p,t,n)}else{return k.measure(p,t)}}else{return k.measure(p)}}else{if(k.webkitMeasure){return k.webkitMeasure(p,t,n)}}}var r=0,o=f();if(t){var s=h(t);if(s){r=s.startTime}else{if(k&&k.timing&&k.timing[t]){r=k.timing[t]-k.timing.navigationStart}else{return}}}if(n){var q=h(n);if(q){o=q.startTime}else{if(k&&k.timing&&k.timing[n]){o=k.timing[n]-k.timing.navigationStart}else{return}}}d.push({name:p,entryType:"measure",startTime:r,duration:(o-r)});return}function h(n){return c(n,g())}function c(p,o){for(i=o.length-1;i>=0;i--){var n=o[i];if(p===n.name){return n}}return undefined}function g(){if(k){if(k.getEntriesByType){return k.getEntriesByType("mark")}else{if(k.webkitGetEntriesByType){return k.webkitGetEntriesByType("mark")}}}return a}return{mark:b,measure:m,gaMarks:a,gaMeasures:d}})();LUX.ns=(Date.now?Date.now():+(new Date()));LUX.ac=[];LUX.cmd=function(a){LUX.ac.push(a)};LUX.init=function(){LUX.cmd(["init"])};LUX.send=function(){LUX.cmd(["send"])};LUX.addData=function(a,b){LUX.cmd(["addData",a,b])};LUX_ae=[];window.addEventListener("error",function(a){LUX_ae.push(a)});LUX_al=[];if("function"===typeof(PerformanceObserver)&&"function"===typeof(PerformanceLongTaskTiming)){var LongTaskObserver=new PerformanceObserver(function(c){var b=c.getEntries();for(var a=0;a<b.length;a++){var d=b[a];LUX_al.push(d)}});try{LongTaskObserver.observe({type:["longtask"]})}catch(e){}};</script><script src="https://cdn.speedcurve.com/js/lux.js?id=4263785399" defer crossorigin="anonymous">;`
          }}
        ></script>
      )}
    </>
  )
}

export default MyHead
